// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

import { BffWebSocketClient, UserCreatedEvent, getSharedBffWebSocket } from '@/services/bffWebSocket';
import { incrementCounter } from '@/services/appMetrics';
import { loginWithPassword } from '@/auth/oidc';
import { useI18n } from '@/i18n';

const MIN_PASSWORD_LENGTH = 6;
// Keycloak provisioning plus a cold service start can comfortably exceed 15s, and this timeout
// is now the only thing that gives up on a request — see the disconnect handling below.
const REGISTER_REQUEST_TIMEOUT_MS = 30000;

type UseRegisterUserParams = {
  apiBaseUrl: string;
  isFocused: boolean;
  displayName: string;
  email: string;
  password: string;
  claimToken?: string | null;
  onReset?: () => void;
  onSuccess?: () => void;
};

export const useRegisterUser = ({
  apiBaseUrl,
  isFocused,
  displayName,
  email,
  password,
  claimToken,
  onReset,
  onSuccess,
}: UseRegisterUserParams) => {
  const { t } = useI18n();
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [socketState, setSocketState] = useState<'connected' | 'disconnected'>('disconnected');
  const [canRecoverByLogin, setCanRecoverByLogin] = useState(false);
  const requestIdRef = useRef<string | null>(null);
  const socketRef = useRef<BffWebSocketClient | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onResetRef = useRef(onReset);
  const onSuccessRef = useRef(onSuccess);
  // Read inside the socket handler, which is bound once — refs keep it seeing current input.
  const credentialsRef = useRef({ email, password });

  useEffect(() => {
    credentialsRef.current = { email, password };
  }, [email, password]);

  useEffect(() => {
    onResetRef.current = onReset;
  }, [onReset]);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  const clearPendingTimeout = useCallback(() => {
    if (!pendingTimeoutRef.current) return;
    clearTimeout(pendingTimeoutRef.current);
    pendingTimeoutRef.current = null;
  }, []);

  const startPendingTimeout = useCallback(() => {
    clearPendingTimeout();
    pendingTimeoutRef.current = setTimeout(() => {
      if (!requestIdRef.current) return;
      console.warn('[register] user.create timed out', { requestId: requestIdRef.current });
      requestIdRef.current = null;
      setLoading(false);
      setStatus(null);
      setError(t('register.createTimeout'));
      incrementCounter('goffy.frontend.register.count', 1, { outcome: 'failed', reason: 'timeout' });
    }, REGISTER_REQUEST_TIMEOUT_MS);
  }, [clearPendingTimeout, t]);

  useEffect(() => {
    const client = getSharedBffWebSocket(apiBaseUrl);
    socketRef.current = client;
    client.setAnonymousReadOnly(true);
    const handleInboundMessage = (message: UserCreatedEvent) => {
      if (message?.type !== 'user.created') return;
      if (!requestIdRef.current || message.requestId !== requestIdRef.current) return;
      console.info('[register] user.created received', {
        requestId: message.requestId,
        queued: message.queued,
        success: message.success,
        error: message.error,
      });

      if (message.queued) {
        setStatus(t('register.statusQueued'));
        setError(null);
        setLoading(true);
        startPendingTimeout();
        return;
      }

      clearPendingTimeout();
      if (message.success === false) {
        const errorKey = message.error;
        if (errorKey === 'user.exists' || errorKey === 'user.email.exists') {
          void recoverFromExistingUser();
          return;
        }

        setLoading(false);
        if (errorKey === 'register.claimProofInvalid') {
          setError(t('register.claimProofInvalid'));
        } else if (errorKey === 'tour.member.claim.sourceNotUnclaimed') {
          setError(t('tour.member.claim.sourceNotUnclaimed'));
        } else {
          setError(t('register.createFailed'));
        }
        incrementCounter('goffy.frontend.register.count', 1, { outcome: 'failed', reason: normalizeReason(errorKey) });
        setStatus(null);
        requestIdRef.current = null;
        return;
      }

      setLoading(false);
      setStatus(t('register.statusSuccess'));
      setError(null);
      incrementCounter('goffy.frontend.register.count', 1, { outcome: 'succeeded' });
      onResetRef.current?.();
      onSuccessRef.current?.();
      requestIdRef.current = null;
    };

    /**
     * "That email already exists" is the dead end this flow used to leave people in: a request
     * that actually landed but whose reply was lost (a reconnect, a backgrounded app) comes back
     * as a duplicate on the retry, and the account genuinely is theirs. Try their own credentials
     * before showing an error — if they work, the registration succeeded and we just continue.
     */
    const recoverFromExistingUser = async () => {
      const { email: pendingEmail, password: pendingPassword } = credentialsRef.current;
      try {
        await loginWithPassword(pendingEmail.trim(), pendingPassword);
      } catch {
        setLoading(false);
        setError(t('register.userExists'));
        setCanRecoverByLogin(true);
        incrementCounter('goffy.frontend.register.count', 1, { outcome: 'failed', reason: 'user.exists' });
        setStatus(null);
        requestIdRef.current = null;
        return;
      }

      setLoading(false);
      setStatus(t('register.statusSuccess'));
      setError(null);
      incrementCounter('goffy.frontend.register.count', 1, { outcome: 'succeeded', reason: 'recovered' });
      onResetRef.current?.();
      onSuccessRef.current?.();
      requestIdRef.current = null;
    };

    const unsubscribe = client.onMessage((msg) => handleInboundMessage(msg as UserCreatedEvent));
    const onConn = client.onConnectionChange((state) => {
      setSocketState(state);
      // A drop is NOT a failure: the ws client queues the frame and flushes it on reconnect, so
      // the request is very likely still in flight. Failing here used to send people straight
      // into a retry that came back "email already exists" — with no way forward. The pending
      // timeout is the only thing that gives up.
      if (state === 'disconnected' && requestIdRef.current) {
        console.warn('[register] BFF disconnected while user.create pending', { requestId: requestIdRef.current });
        setStatus(t('register.statusReconnecting'));
      }
    });
    if (isFocused) {
      client.setSubscriptionContext({ roundId: null, tourId: null });
    }
    return () => {
      unsubscribe();
      onConn();
      clearPendingTimeout();
      const path = typeof window !== 'undefined' ? window.location?.pathname ?? '' : '';
      if (!path.startsWith('/register')) {
        client.setAnonymousReadOnly(false);
      }
    };
  }, [apiBaseUrl, clearPendingTimeout, isFocused, startPendingTimeout, t]);

  const sendCreateRequest = useCallback(() => {
    if (!email.trim() || !displayName.trim() || !password) {
      setError(t('register.validationMissing'));
      incrementCounter('goffy.frontend.register.count', 1, { outcome: 'failed', reason: 'validation' });
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('register.validationPasswordTooShort', { min: MIN_PASSWORD_LENGTH }));
      incrementCounter('goffy.frontend.register.count', 1, { outcome: 'failed', reason: 'validation' });
      return;
    }
    if (!socketRef.current) {
      setError(t('register.connectionMissing'));
      incrementCounter('goffy.frontend.register.count', 1, { outcome: 'failed', reason: 'no.socket' });
      return;
    }

    const requestId = generateRequestId();
    requestIdRef.current = requestId;
    setStatus(t('register.statusQueued'));
    setError(null);
    setCanRecoverByLogin(false);
    setLoading(true);
    startPendingTimeout();
    // Signup drop-off is otherwise invisible: the register socket is anonymous, and the BFF
    // drops log.emit from anonymous connections. metric.emit is on its allowlist.
    incrementCounter('goffy.frontend.register.count', 1, { outcome: 'attempted' });
    console.info('[register] sending user.create', {
      requestId,
      email: email.trim(),
      displayName: displayName.trim(),
    });
    // 'queued' is not a failure — the frame is flushed when the socket (re)opens and the
    // request is still in flight; the pending timeout armed above is the only thing that gives up.
    socketRef.current.sendUserCreate({
      displayName: displayName.trim(),
      password,
      email: email.trim(),
      requestId,
      claimToken: claimToken ?? undefined,
    });
  }, [claimToken, displayName, email, password, startPendingTimeout, t]);

  return {
    status,
    error,
    loading,
    socketState,
    /** True when the failure was "already exists" and their password did not open it. */
    canRecoverByLogin,
    sendCreateRequest,
  };
};

const normalizeReason = (error: string | undefined): string => {
  if (!error) return 'unknown';
  return error.replace(/[^a-zA-Z0-9._-]/g, '.').slice(0, 40);
};

const generateRequestId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  return `req-${Date.now()}`;
};
